/**
 * Promo-site runtime configuration for the embedded LIVE widget.
 *
 * Mirrors the demo-host convention: query params override local-first defaults
 * so the page can be repointed at any backend / widget bundle / public key
 * without rebuilding:
 *
 *   ?apiBase=http://localhost:8003   backend serving /v1/config|/v1/feedback
 *   ?publicKey=pk_demo_local         widget-instance public key to embed
 *   ?widgetSrc=/vendor/hotline.js    URL of the built widget ESM bundle
 *
 * In production builds the defaults point at the hosted Hotline endpoints
 * (override at build time via VITE_API_BASE / VITE_PUBLIC_KEY / VITE_WIDGET_SRC).
 * Only the PUBLIC key (`pk_*`) ever reaches the browser.
 */

interface PromoEnv {
  PROD?: boolean;
  VITE_API_BASE?: string;
  VITE_PUBLIC_KEY?: string;
  VITE_WIDGET_SRC?: string;
}

export interface PromoWidgetConfig {
  apiBase: string;
  publicKey: string;
  widgetSrc: string;
}

function stripTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/** Resolve the widget config from query params + env defaults. Pure for tests. */
export function resolvePromoConfig(
  search: string,
  env: PromoEnv = import.meta.env as PromoEnv,
): PromoWidgetConfig {
  const prodDefaults = {
    apiBase: env.VITE_API_BASE ?? 'https://api.hotline.red',
    publicKey: env.VITE_PUBLIC_KEY ?? 'pk_demo_local',
    widgetSrc: env.VITE_WIDGET_SRC ?? '/vendor/hotline.js',
  };
  const devDefaults = {
    apiBase: env.VITE_API_BASE ?? 'http://localhost:8003',
    publicKey: env.VITE_PUBLIC_KEY ?? 'pk_demo_local',
    widgetSrc: env.VITE_WIDGET_SRC ?? '/vendor/hotline.js',
  };
  const d = env.PROD ? prodDefaults : devDefaults;
  const params = new URLSearchParams(search);
  return {
    apiBase: stripTrailingSlash(params.get('apiBase') ?? d.apiBase),
    publicKey: params.get('publicKey') ?? d.publicKey,
    widgetSrc: params.get('widgetSrc') ?? d.widgetSrc,
  };
}

/**
 * Inject the REAL widget exactly like a customer embed: a module <script> with
 * the data-* config attributes (see apps/widget/src/config.ts discovery).
 */
export function embedLiveWidget(cfg: PromoWidgetConfig, doc: Document = document): HTMLScriptElement {
  const script = doc.createElement('script');
  script.type = 'module';
  script.src = cfg.widgetSrc;
  script.async = true;
  script.setAttribute('data-public-key', cfg.publicKey);
  script.setAttribute('data-api-base', cfg.apiBase);
  script.setAttribute('data-mode', 'floating');
  script.setAttribute('data-testid', 'hotline-widget-script');
  doc.body.appendChild(script);
  return script;
}
